import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './Dashboard';
import Superadmin from './pages/Superadmin';
import { authService } from './services/auth.service';
import ErreurReseau from './components/ErreurReseau';

declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(false);
  const [connecte, setConnecte] = useState(authService.isAuthenticated());

  // Connexion Google (Google Identity Services). N'est activée que si un
  // REACT_APP_GOOGLE_CLIENT_ID est fourni au build ; sinon le bouton ne s'affiche pas.
  useEffect(() => {
    if (connecte || !GOOGLE_CLIENT_ID) return;

    const handleGoogleCredential = async (response: any) => {
      setErreur('');
      setChargement(true);
      try {
        await authService.loginGoogle(response.credential);
        setConnecte(true);
      } catch (err: any) {
        const msg = err?.response?.data?.message;
        setErreur(typeof msg === 'string' ? msg : 'Connexion Google refusée');
      } finally {
        setChargement(false);
      }
    };

    const initGoogle = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      const cible = document.getElementById('google-btn');
      if (cible) {
        window.google.accounts.id.renderButton(cible, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          locale: 'fr',
          width: 320,
        });
      }
    };

    if (window.google) {
      initGoogle();
      return;
    }
    const scriptId = 'google-identity-services';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.body.appendChild(script);
  }, [connecte]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    setChargement(true);
    try {
      await authService.login(email, password);
      setConnecte(true);
    } catch (err: any) {
      // Affiche le message du serveur (ex : licence expirée/suspendue) si présent.
      const msg = err?.response?.data?.message;
      setErreur(typeof msg === 'string' ? msg : 'Email ou mot de passe incorrect');
    } finally {
      setChargement(false);
    }
  };

  if (connecte) {
    const user = authService.getUser();
    if (user?.role === 'superadmin') {
      return (
        <>
          <ErreurReseau />
          <Superadmin />
        </>
      );
    }
    // L'application complète est désormais responsive (téléphone → grand écran).
    return (
      <>
        <ErreurReseau />
        <Dashboard />
      </>
    );
  }

  return (
    <div className="nexacom-app">
      <ErreurReseau />
      <div className="nexacom-login">
        <div className="nexacom-logo">
          <h1>NexaCom</h1>
          <p>Solution de gestion commerciale</p>
        </div>
        <form className="nexacom-form" onSubmit={handleLogin}>
          <h2>Connexion</h2>
          {erreur && <div className="erreur-msg">⚠️ {erreur}</div>}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="votre@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </div>
          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <button
            className="btn-connexion"
            type="submit"
            disabled={chargement}>
            {chargement ? 'Connexion...' : 'Se connecter'}
          </button>

          {GOOGLE_CLIENT_ID && (
            <>
              <div className="login-separateur"><span>ou</span></div>
              <div id="google-btn" className="google-btn-wrap" />
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default App;